import { useEffect, useRef } from 'react';
import type { Task, ListTasksResponse } from '../types/task';

/**
 * Polls workstream tasks on an interval and merges server state into the
 * local task list without flicker (no setTasks([])).
 *
 * Merge strategy:
 * - Tasks that exist locally and on the server: update if server version
 *   is higher, otherwise keep local (preserves optimistic updates).
 * - Tasks that only exist on the server: append (new tasks created
 *   elsewhere or by the agent).
 * - Tasks that only exist locally: keep (may be optimistic creates not
 *   yet visible on the server).
 * - Server ordering is authoritative: the merged list follows server
 *   order for tasks present on the server, then appends local-only tasks.
 *
 * The hook skips polling when:
 * - workstreamID is falsy
 * - enabled is false (e.g. card is collapsed or off-screen)
 * - a previous poll is still in-flight
 */
export default function useTaskPolling(
  workstreamID: string | undefined,
  tasksRef: React.RefObject<Task[]>,
  setTasks: (updater: (prev: Task[]) => Task[]) => void,
  enabled: boolean = true,
  intervalMs: number = 5000,
) {
  const refreshingRef = useRef(false);
  const generationRef = useRef(0);

  useEffect(() => {
    if (!workstreamID || !enabled) return;

    generationRef.current++;
    const gen = generationRef.current;

    const poll = async () => {
      if (refreshingRef.current) return;
      if (gen !== generationRef.current) return;
      refreshingRef.current = true;
      try {
        const baseUrl = `/api/workstreams/${encodeURIComponent(workstreamID)}/tasks?maxResults=200&includeDeleted=true`;
        const serverTasks: Task[] = [];
        let url = baseUrl;
        while (url) {
          const resp = await fetch(url);
          if (gen !== generationRef.current || !resp.ok) return;
          const data: ListTasksResponse = await resp.json();
          serverTasks.push(...(data.Tasks ?? []));
          url = data.NextToken
            ? `${baseUrl}&token=${encodeURIComponent(data.NextToken)}`
            : '';
        }
        if (gen !== generationRef.current) return;

        setTasks((prev) => mergeTasks(prev, serverTasks));
      } catch {
        // Silently ignore — next poll will retry.
      } finally {
        refreshingRef.current = false;
      }
    };

    const id = window.setInterval(poll, intervalMs);
    return () => {
      generationRef.current++;
      window.clearInterval(id);
    };
  }, [workstreamID, enabled, intervalMs, setTasks, tasksRef]);
}

/**
 * Merge server tasks into the local list.
 *
 * When any task has a newer server version (actual data change), the
 * merged list uses server ordering — which is authoritative for rank.
 * When NO task has a newer server version, the local array is returned
 * unchanged so optimistic reorders (which keep the same version until
 * the PATCH response arrives) are not reverted by a poll cycle.
 */
function mergeTasks(local: Task[], server: Task[]): Task[] {
  const localByID = new Map<string, Task>();
  for (const t of local) localByID.set(t.TaskID, t);

  // Check whether the server has any newer data. Only tasks that
  // exist on the server with a higher version or that are new on the
  // server (not in local) count as updates. Local-only tasks
  // (optimistic creates not yet on the server) are NOT counted — they
  // should not force the server-order merge path.
  let hasServerUpdate = false;
  for (const st of server) {
    const lt = localByID.get(st.TaskID);
    if (!lt || (st.Version ?? 0) > (lt.Version ?? 0)) {
      hasServerUpdate = true;
      break;
    }
  }

  if (!hasServerUpdate) {
    // Nothing changed — keep local array as-is, preserving optimistic
    // reorder and avoiding re-renders.
    return local;
  }

  // Build merged list in server order, keeping local objects when
  // their version is >= the server's.
  const merged: Task[] = [];
  const seen = new Set<string>();

  for (const st of server) {
    seen.add(st.TaskID);
    const lt = localByID.get(st.TaskID);
    if (lt) {
      merged.push((lt.Version ?? 0) >= (st.Version ?? 0) ? lt : st);
    } else {
      merged.push(st);
    }
  }

  // Append local-only tasks (optimistic creates not yet on server).
  for (const lt of local) {
    if (!seen.has(lt.TaskID)) merged.push(lt);
  }

  return merged;
}
